import {
  disableJiraWebhook,
  enableJiraWebhook,
  getJiraWebhook,
  rotateJiraWebhook,
  type JiraWebhookConfig,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

/**
 * Per-intake-agent Jira webhook config.
 *
 * `config.webhook_url` embeds the webhook's token, and admin-registered Jira webhooks are
 * unsigned — that token is the entire authenticity check. Treat it like an API key: never log it,
 * never navigate to it, never put it in analytics.
 */
export async function fetchJiraWebhookConfig(intakeAgentUid: string): Promise<JiraWebhookConfig> {
  const response = await getJiraWebhook({ path: { intake_agent_uid: intakeAgentUid } });

  if (response.error) {
    throw new Error(
      extractErrorDetail(response.error, 'Failed to load the Jira webhook configuration.')
    );
  }

  return response.data;
}

/**
 * Mint a token if the agent has none, otherwise keep the existing one — so a URL already
 * registered in Jira keeps working across a disable/enable cycle. Idempotent.
 */
export async function enableAgentJiraWebhook(intakeAgentUid: string): Promise<JiraWebhookConfig> {
  const response = await enableJiraWebhook({ path: { intake_agent_uid: intakeAgentUid } });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to enable the Jira webhook.'));
  }

  return response.data;
}

/**
 * Replace the token. The old URL dies immediately, so the new one has to be pasted into Jira
 * before events flow again.
 */
export async function rotateAgentJiraWebhook(intakeAgentUid: string): Promise<JiraWebhookConfig> {
  const response = await rotateJiraWebhook({ path: { intake_agent_uid: intakeAgentUid } });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to rotate the Jira webhook.'));
  }

  return response.data;
}

export async function disableAgentJiraWebhook(intakeAgentUid: string): Promise<JiraWebhookConfig> {
  const response = await disableJiraWebhook({ path: { intake_agent_uid: intakeAgentUid } });

  if (response.error) {
    throw new Error(extractErrorDetail(response.error, 'Failed to disable the Jira webhook.'));
  }

  return response.data;
}
